// NRA Precision Pistol (February 2026), Section 15. Numeric shot-array order is
// NOT firing order: Range Pocket records whole scored targets (Rule 15.3(f)).
export const BULLSEYE_TIE_RULEBOOK = "https://competitions.nra.org/media/10579/2026-precisionpistol_rulebook.pdf#page=44";

export function bullseyeTieContext(row, sectionKey = "match::aggregate", kind = "match", settings) {
  const courses = (row.firearms || []).flatMap(firearm => (firearm.courses || []).map(course => ({
    key: `course::${firearm.gunGroupId}::${course.courseName}`,
    firearmKey: `firearm::${firearm.gunGroupId}`,
    title: `${firearm.firearmTitle} ${course.courseName}`,
    units: course.scoreUnits || [],
  })));
  return { sectionKey, kind, courses, firingOrder: settings?.tieBreakFiringOrder || [] };
}

function sum(units, field) { return units.reduce((total, unit) => total + Number(unit[field] || 0), 0); }
function validUnit(unit) {
  const evidence = unit.tieBreak;
  const counts = evidence?.ringCounts;
  return evidence?.version === 1 && evidence.complete === true && evidence.scoredAs === "target"
    && ["slow", "timed", "rapid"].includes(unit.fireType)
    && Array.isArray(counts) && counts.length === 12 && counts.every(n => Number.isInteger(n) && n >= 0)
    && counts.reduce((a, b) => a + b, 0) === Number(unit.plannedShots)
    && Number(unit.plannedShots) > 0 && Number(unit.shotsFired) === Number(unit.plannedShots)
    && counts.reduce((n, count, ring) => n + count * Math.min(ring, 10), 0) === Number(unit.totalScore)
    && counts[11] === Number(unit.xCount);
}

export function validBullseyeFiringOrder(context) {
  const keys = context.courses.map(course => course.key);
  return keys.length > 0 && context.firingOrder.length === keys.length
    && new Set(context.firingOrder).size === keys.length && keys.every(key => context.firingOrder.includes(key));
}

// Build a lexicographic sequence. A missing criterion is a STOP, never a zero.
// This is evaluated for an entire tied cohort to avoid non-transitive pairwise
// sorting when only some competitors have complete evidence.
export function bullseyeTieSteps(row) {
  const context = row.tieContext || bullseyeTieContext(row);
  const steps = [];
  const add = (key, value, rule, label) => steps.push({ key, value, rule, label });
  const stop = (key, message) => add(key, null, "", message);
  const score = (units, prefix, rule, label) => {
    add(`${prefix}:score`, sum(units, "totalScore"), rule, `${label} score`);
    add(`${prefix}:x`, sum(units, "xCount"), rule, `${label} X count`);
  };
  const hits = (units, prefix, rulePrefix = "") => {
    for (let ring = 0; ring <= 10; ring += 1) {
      const rule = ring === 0 ? "15.3(b)" : ring === 1 ? "15.3(c)" : "15.3(c)-(d)";
      add(`${prefix}:ring:${ring}`, -units.reduce((n, unit) => n + unit.tieBreak.ringCounts[ring], 0),
        rulePrefix ? `${rulePrefix} / ${rule}` : rule, ring === 0 ? "Fewer misses" : `Fewer ${ring}-point hits`);
    }
  };
  const single = (units, prefix, ordered, rulePrefix = "") => {
    hits(units, prefix, rulePrefix);
    if (!ordered) { stop(`${prefix}:order`, "Confirm the actual firing order in Tie Review."); return; }
    if (units.some(unit => ![5, 10].includes(Number(unit.plannedShots)))) {
      stop(`${prefix}:strings`, "Scored 5- or 10-shot string detail is required."); return;
    }
    [...units].reverse().forEach((unit, index) => {
      const rule = rulePrefix ? `${rulePrefix} / 15.3(f)` : "15.3(f)";
      const key = `${prefix}:last:${index}:${unit.unitKey}`;
      score([unit], key, rule, index === 0 ? "Last scored string" : `Earlier scored string ${index + 1}`);
      hits([unit], key, rule);
    });
  };
  const rankedCourse = (units, prefix, rulePrefix = "") => {
    const types = new Set(units.map(unit => unit.fireType));
    if (types.size === 1) { single(units, prefix, true, rulePrefix); return; }
    for (const [type, clause] of [["rapid", "b"], ["timed", "c"], ["slow", "d"]]) {
      const selected = units.filter(unit => unit.fireType === type);
      if (!selected.length) continue;
      if (new Set(selected.map(unit => unit.stageId)).size > 1) {
        stop(`${prefix}:${type}:stages`, "Multiple same-type stages require the director to verify the match structure."); return;
      }
      const rule = `${rulePrefix ? `${rulePrefix} / ` : ""}15.4(${clause})`;
      score(selected, `${prefix}:${type}`, rule, `${type[0].toUpperCase()}${type.slice(1)} fire`);
      single(selected, `${prefix}:${type}`, true, rule);
    }
  };
  const ordered = validBullseyeFiringOrder(context);
  const courses = ordered ? context.firingOrder.map(key => context.courses.find(course => course.key === key)) : context.courses;
  const selected = courses.filter(course => context.kind === "match" ||
    (context.kind === "firearm" ? course.firearmKey === context.sectionKey : course.key === context.sectionKey));
  const units = selected.flatMap(course => course.units);
  if (!units.length || units.some(unit => !validUnit(unit))
    || sum(units, "totalScore") !== Number(row.totalScore) || sum(units, "xCount") !== Number(row.xCount)) {
    stop("evidence", "Complete, reconciled target scores are required. Review the scorecards and sync corrections.");
    return steps;
  }
  add(`program:${JSON.stringify(units.map(unit => [unit.unitKey, unit.fireType, unit.plannedShots]))}`, 0, "", "Comparable scoring programs");
  if (context.kind === "course") rankedCourse(units, "course");
  else {
    for (const [type, clause] of [["rapid", "b"], ["timed", "c"], ["slow", "d"]]) {
      const typeUnits = units.filter(unit => unit.fireType === type);
      if (!typeUnits.length) continue;
      const rule = `15.5(${clause})`;
      score(typeUnits, `aggregate:${type}`, rule, `Total ${type} fire`);
      const typeCourseCount = selected.filter(course => course.units.some(unit => unit.fireType === type)).length;
      single(typeUnits, `aggregate:${type}`, ordered || typeCourseCount === 1, rule);
    }
  }
  if (!ordered) { stop("15.10:order", "Confirm the actual firing order in Tie Review (Rule 15.10)."); return steps; }
  // Never infer chronology from upload timestamps or alphabetical course names.
  const index = context.kind === "course" ? courses.findIndex(course => course.key === context.sectionKey)
    : Math.max(...selected.map(course => courses.indexOf(course))) + 1;
  const preceding = index > 0 ? courses.slice(0, index).reverse() : courses.slice(1);
  for (const course of preceding) {
    const prefix = `15.10:${course.key}`;
    if (!course.units.length || course.units.some(unit => !validUnit(unit))) {
      stop(prefix, `Rule 15.10 needs complete scores for ${course.title}.`); return steps;
    }
    score(course.units, prefix, "15.10", course.title);
    rankedCourse(course.units, prefix, "15.10");
  }
  stop("15.10:remaining", "The recorded matches still tie under Section 15. The match director must review the source scores.");
  return steps;
}

export function resolveBullseyeTieGroup(rows) {
  if (rows.length < 2) return [{ rows }];
  const sequences = new Map(rows.map(row => [row, bullseyeTieSteps(row)]));
  const split = (group, index, decision) => {
    if (group.length === 1) return [{ rows: group, decision }];
    const criteria = group.map(row => sequences.get(row)[index]);
    if (criteria.some(step => !step || step.value == null || step.key !== criteria[0]?.key)) {
      return [{ rows: group, pending: criteria.find(step => step?.value == null)?.label || "Comparable target detail is required." }];
    }
    const values = [...new Set(criteria.map(step => step.value))].sort((a, b) => b - a);
    if (values.length === 1) return split(group, index + 1, decision);
    return values.flatMap(value => split(group.filter(row => sequences.get(row)[index].value === value), index + 1, { ...criteria[0], value }));
  };
  return split(rows, 0, null);
}
